import React, { Fragment } from 'react';
import { NavLink } from 'redux-first-router-link';
import PropTypes from 'prop-types';
import { getFileName, isValid, segmentTrackEvent, scrollToTopOf } from '../../utils';
import { SOCIALS_MAP, PODCASTS_MAP } from '../../consts';

const Byline = ({
  imgURL,
  imgAlt,
  name,
  link,
  nameTwitter,
  nameInstagram,
  pubData,
  sponsorship,
  showEditorByline,
  secondaryAuthor,
  secondAuthorLink,
  secondAuthorText,
  additionalAuthors,
  display,
  isMobile,
  isRecipe,
  isSponsored,
  togglePrint,
  hasContentHeader
}) => {
  const {
    sponsorshipUrl = null,
    sponsorshipBannerOverride = null,
    sponsorshipImage = null,
    sponsorshipImageAlt = null,
    articleType = null,
  } = sponsorship;

  const {
    date = null,
    first = null,
  } = pubData;

  const showDate = (isValid(display.date) && (display.date === '1'));
  const showTime = (isValid(display.time) && (display.time === '1'));
  const showAuthor = (isValid(display.author) && (display.author === '1'));
  const showSlugs = (isValid(display.taggedSlugs) && (display.taggedSlugs === '1'));
  const noAuthor = (!isValid(name));
  const secondAuthorName = isValid(secondaryAuthor) ? secondaryAuthor.name : null;

  if (!showAuthor && !showSlugs && !showDate && !showTime) {
    return null;
  }

  const dividerDot = (
    <Fragment>
      <span className="dot">&#8226;</span>
    </Fragment>
  );

  // DATE & TIME (PUBLISHED, UPDATED)
  const renderDate = (date) => {
    let options = {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    };
    const renderedDate = (date && showDate) ? new Date(date.replace(/\s/, 'T')).toLocaleString('en-us', options) : null;
    return renderedDate;
  };
  const pubDate = first ? new Date(first.replace(/\s/, 'T')) : date ? new Date(date.replace(/\s/, 'T')) : null;
  const upDate = date ? new Date(date.replace(/\s/, 'T')) : null;
  const renderedPubDate = first ? renderDate(first) : date ? renderDate(date) : null;
  const renderedUpDate = date ? renderDate(date) : null;

  let renderedPubTime = pubDate ? pubDate.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  }) : null;
  // remove the leading 0 if hour is less than 10, make am/pm lowercase, removes whitespace between time -> am/pm
  renderedPubTime = renderedPubTime ? renderedPubTime
    .toLowerCase()
    .replace(/^0(?:0:0?)?/, '')
    .replace(/\s/, '') : null;

  let renderedUpTime = upDate ? upDate.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  }) : null;
  // remove the leading 0 if hour is less than 10, make am/pm lowercase, removes whitespace between time -> am/pm
  renderedUpTime = renderedUpTime ? renderedUpTime
    .toLowerCase()
    .replace(/^0(?:0:0?)?/, '')
    .replace(/\s/, '') : null;

  let dateLine = null;
  if (showDate) {
    dateLine = (upDate && (upDate > pubDate)) ? `Updated ${renderedUpDate}` : `Published ${renderedPubDate}`;
  }
  // if isMobile && showSecondAuthor, do not show time
  let timeLine = null;
  if (showTime) {
    timeLine = (upDate && (upDate > pubDate)) ? `${renderedUpTime}` : `${renderedPubTime}`;
  }

  const renderedDateLine = <div className="pub-date">{dateLine}</div>;
  const renderedTimeLine = <><div className="pub-date pub-time">{timeLine}</div></>;


  // if no author is selected, render an error msg in place of the Byline that alerts the Editor to choose an Author from the dropdown
  const noAuthorError = 'Error: You must select an Author/Contributor in order to display the Byline';
  const renderedErrorMessage = <div className="no-author-error">{noAuthorError}</div>;


  // PODCAST SLUGS
  // let podcast = null;
  // if (tags.length > 0) {
  //   const podTags = tags.filter(tag => (PODCASTS_MAP[tag]));
  //   if (podTags.length > 0 && showSlugs) {
  //     const podTag = podTags[0];
  //     podcast = PODCASTS_MAP[podTag];
  //   }
  // }
  // const renderedPodcastSlug = (
  //   <Fragment>
  //   { podcast &&
  //     <div className="podcast-slug"><a href={podcast.sponsorshipUrl} target="_blank"><img src={podcast.sponsorshipImage} /></a></div>
  //   }
  //   </Fragment>
  // );

  // Will render author image, author name
  
  let showNameDot = true;
  if (showTime && isMobile) {
    showNameDot = false;
  }

  const renderedEditorByline = (
    <Fragment>
      {showAuthor &&
        <div className="author-container">
            {/* Author image */}
            <img src={imgURL} className="author-img" alt={`Author image: ${imgAlt}`} />
            {/* Author name */}
            <div className="author-text">
              <div className="author" onClick={() => segmentTrackEvent('surround_article', { action: 'author', label: name })}>By <NavLink to={link} target="_blank">{name}</NavLink></div>
            </div>
        </div>
      }
    </Fragment>
  );

  const medicallyReviewedByPolicyPageURL = "https://www.purewow.com/page/medically-reviewed-by-policy";

  // Will render second author info if exists
  const renderedAddtionalAuthorByline = (
    <Fragment>
      { (isValid(additionalAuthors)) &&
        <Fragment>
        { (isValid(additionalAuthors.secondaryAuthor)) &&
          <div className="second-author-container">
            { isValid(additionalAuthors.secondaryAuthor.text) && <div className="second-author-credit">{additionalAuthors.secondaryAuthor.text}</div> }
            { (isValid(additionalAuthors.secondaryAuthor.authors[0]) && isValid(additionalAuthors.secondaryAuthor.authors[0].link)) && <div onClick={() => segmentTrackEvent('surround_article', { action: 'author2', label: additionalAuthors.secondaryAuthor.authors[0].name })}><NavLink to={`/author/${additionalAuthors.secondaryAuthor.authors[0].link}`} className="second-author-name" target="_blank">{additionalAuthors.secondaryAuthor.authors[0].name}</NavLink></div> }
            { (isValid(additionalAuthors.secondaryAuthor.authors[0]) && !isValid(additionalAuthors.secondaryAuthor.authors[0].link)) && <div className="second-author-name">{additionalAuthors.secondaryAuthor.authors[0].name}</div> }
            { isValid(additionalAuthors.secondaryAuthor.authors[1]) && <div className="second-author-name">&</div> }
            { (isValid(additionalAuthors.secondaryAuthor.authors[1]) && isValid(additionalAuthors.secondaryAuthor.authors[1].link)) && <div onClick={() => segmentTrackEvent('surround_article', { action: 'author2', label: additionalAuthors.secondaryAuthor.authors[1].name })}><NavLink to={`/author/${additionalAuthors.secondaryAuthor.authors[1].link}`} target="_blank" className="second-author-name">{additionalAuthors.secondaryAuthor.authors[1].name}</NavLink></div> }
            { (isValid(additionalAuthors.secondaryAuthor.authors[1]) && !isValid(additionalAuthors.secondaryAuthor.authors[1].link)) && <div className="second-author-name">{additionalAuthors.secondaryAuthor.authors[1].name}</div> }
          </div>
        }
        { (isValid(additionalAuthors.tertiaryAuthor)) &&
          <div className="second-author-container">
            { (isValid(additionalAuthors.tertiaryAuthor.text) && (additionalAuthors.tertiaryAuthor.text === 'Medically reviewed by')) ? 
                <div onClick={() => segmentTrackEvent('surround_article', { action: 'author3', label: additionalAuthors.tertiaryAuthor.text })}><a href={medicallyReviewedByPolicyPageURL} target="_blank" className="second-author-credit">{additionalAuthors.tertiaryAuthor.text}</a></div>
              : <div className="second-author-credit">{additionalAuthors.tertiaryAuthor.text}</div> }
            { (isValid(additionalAuthors.tertiaryAuthor.authors[0]) && isValid(additionalAuthors.tertiaryAuthor.authors[0].link)) && <div onClick={() => segmentTrackEvent('surround_article', { action: 'author3', label: additionalAuthors.tertiaryAuthor.authors[0].name })}><NavLink to={`/author/${additionalAuthors.tertiaryAuthor.authors[0].link}`} target="_blank" className="second-author-name">{additionalAuthors.tertiaryAuthor.authors[0].name}</NavLink></div> }
            { (isValid(additionalAuthors.tertiaryAuthor.authors[0]) && !isValid(additionalAuthors.tertiaryAuthor.authors[0].link)) && <div className="second-author-name">{additionalAuthors.tertiaryAuthor.authors[0].name}</div> }
            { isValid(additionalAuthors.tertiaryAuthor.authors[1]) && <div className="second-author-name">&</div> }
            { (isValid(additionalAuthors.tertiaryAuthor.authors[1]) && isValid(additionalAuthors.tertiaryAuthor.authors[1].link)) && <div onClick={() => segmentTrackEvent('surround_article', { action: 'author3', label: additionalAuthors.tertiaryAuthor.authors[1].name })}><NavLink to={`/author/${additionalAuthors.tertiaryAuthor.authors[1].link}`} target="_blank" className="second-author-name">{additionalAuthors.tertiaryAuthor.authors[1].name}</NavLink></div> }
            { (isValid(additionalAuthors.tertiaryAuthor.authors[1]) && !isValid(additionalAuthors.tertiaryAuthor.authors[1].link)) && <div className="second-author-name">{additionalAuthors.tertiaryAuthor.authors[1].name}</div> }
          </div>
        }
        </Fragment>
      }
    </Fragment>
  );
  
  const renderedPrintFriendlyButton = (
    <Fragment>
      <p className={`print-friendly-link ${secondAuthorName ? 'below' : ''}`} onClick={() => {togglePrint()}}>Print Recipe</p>
    </Fragment>
  )
  const offSet = isMobile ? 70 : 120;
  const renderedJumpToRecipe = (
    <Fragment>
      <p className="print-friendly-link" onClick={() => {scrollToTopOf("#recipe-jump", offSet)}}>Jump To Recipe</p>
    </Fragment>
  )
  
  return (
    <div className="byline"> 
      { (!hasContentHeader || isSponsored) && <hr className="dividerLine" /> }
        <>
          <div
            className={`author-and-date ${
              showDate && showTime ? "column" : ""
            }`}
          >
            {renderedEditorByline}
            <div className="date-and-time">
              {showDate && showNameDot && dividerDot}
              {showDate && renderedDateLine}
              {showTime && dividerDot}
              {showTime && renderedTimeLine}
            </div>
            {!additionalAuthors && (
              <div className="print-friendly-container">
                {isRecipe && dividerDot}
                {isRecipe && renderedPrintFriendlyButton}
                {isRecipe && dividerDot}
                {isRecipe && renderedJumpToRecipe}
              </div>
            )}
          </div>

          {additionalAuthors && (
            <>
              <div>{renderedAddtionalAuthorByline}</div>
              <div className="print-friendly-container addit-author">
                {isRecipe && renderedPrintFriendlyButton}
                {isRecipe && dividerDot}
                {isRecipe && renderedJumpToRecipe}
              </div>
            </>
          )}
        </>
    </div>
  );
};

Byline.propTypes = {
  imgURL: PropTypes.string,
  imgAlt: PropTypes.string,
  name: PropTypes.string,
  link: PropTypes.string,
  nameTwitter: PropTypes.string,
  nameInstagram: PropTypes.string,
  pubData: PropTypes.string,
  tags: PropTypes.array,
  secondAuthorName: PropTypes.string,
  secondAuthorText: PropTypes.string,
  secondAuthorLink: PropTypes.string,
  isMobile: PropTypes.boolean,
};

export default Byline;
